import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { DonorDetailModal } from "@/components/fundraising/DonorDetailModal";
import { DonorList } from "@/components/fundraising/DonorList";
import { FundraisingPageHeader } from "@/components/fundraising/FundraisingPageHeader";
import { useSetPageContext } from "@/hooks/use-page-context";
import { useSession } from "@/hooks/use-session";

type LegacyFundraisingTab = "donations" | "donors";

function toId(v: unknown): number | undefined {
	const n = Number(v);
	return Number.isFinite(n) && n > 0 ? n : undefined;
}

export const Route = createFileRoute("/_app/_/fundraising/")({
	component: FundraisingDonorsPage,
	// `?tab=donors` was the deep-link from the old internal Tabs UI (now
	// ignored — Donors is the index route). `?donorId` / `?contactId` drive
	// the donor-detail modal so refresh / back / shared links keep working;
	// the deprecated /fundraising/donors/$donorId route redirects here.
	// All keys optional so existing `to="/fundraising"` callers (e.g. the
	// contact-import success redirect) don't have to pass them.
	validateSearch: (
		search: Record<string, unknown>,
	): {
		tab?: LegacyFundraisingTab;
		donorId?: number;
		contactId?: number;
	} => {
		const valid: LegacyFundraisingTab[] = ["donations", "donors"];
		const out: {
			tab?: LegacyFundraisingTab;
			donorId?: number;
			contactId?: number;
		} = {};
		if (valid.includes(search.tab as LegacyFundraisingTab)) {
			out.tab = search.tab as LegacyFundraisingTab;
		}
		const donorId = toId(search.donorId);
		if (donorId !== undefined) out.donorId = donorId;
		const contactId = toId(search.contactId);
		if (contactId !== undefined) out.contactId = contactId;
		return out;
	},
});

function FundraisingDonorsPage() {
	useSetPageContext({
		page_key: "fundraising.donors",
		page_title: "Donors",
		available_actions: ["create_task", "generate_content"],
		current_data_summary: null,
	});

	const session = useSession();
	const campaignId = session.campaign?.id;
	const navigate = useNavigate({ from: Route.fullPath });
	const { donorId, contactId } = Route.useSearch();

	if (!campaignId) {
		return (
			<div className="p-6 text-muted-foreground">No campaign selected.</div>
		);
	}

	return (
		<div className="mx-auto max-w-7xl space-y-6 p-6">
			<FundraisingPageHeader
				title="Donors"
				subtitle="Track your donors and their giving history."
				campaignId={campaignId}
			/>
			<DonorList
				campaignId={campaignId}
				onSelect={(d) =>
					navigate({
						search: (prev) => ({
							...prev,
							donorId: d.donor_id ?? undefined,
							contactId:
								d.donor_id == null ? (d.contact_id ?? undefined) : undefined,
						}),
					})
				}
			/>
			<DonorDetailModal
				campaignId={campaignId}
				donorId={donorId}
				contactId={contactId}
				open={donorId != null || contactId != null}
				onOpenChange={(o) => {
					if (!o)
						navigate({
							search: (prev) => ({
								...prev,
								donorId: undefined,
								contactId: undefined,
							}),
						});
				}}
			/>
		</div>
	);
}
